import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MARKET_EXECUTION_REPORT,
  MarketExecutionReport,
  MixpanelEvent,
  Toggle,
  WSBlotterTableMaxRecordsReachedWarning,
  filterByColumnMainMenuItems,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMarketExecutionReportColumns,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterTableSort,
  type Column,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';
import { colIDToFilterBuilderKey } from '../ExecutionReports/useExecutionReportFilter';
import { useMarketExecutionReportMenu } from './MarketExecutionReportMenu';
import {
  onlyServerFilterKeys,
  useMarketExecutionReportFilter,
  type MarketExecutionReportFilter,
} from './useMarketExecutionReportFilter';

export interface MarketExecutionReportsBlotterTableProps {
  /** Unique id per blotter instance */
  blotterID: string;
  /** ID for common blotter type persistence; usually a subset of the blotterId to represent blotter area 'type' */
  blotterPersistID?: string;
  tabLabel?: string;
  parentOrderID?: string;
  parentRFQID?: string;

  defaultColumns?: (Column | keyof MarketExecutionReport)[];
  defaultFilter?: MarketExecutionReportFilter;
  defaultSort?: BlotterTableSort<MarketExecutionReport>;
  persistFilter?: boolean;
  persistSort?: boolean;
  onCloneTab?: () => void;
  initialIsOpen?: boolean;

  showAllDecimals?: boolean;
  setShowAllDecimals?: (value: boolean) => void;
}

export function MarketExecutionReportsBlotterTable({
  parentOrderID,
  parentRFQID,
  blotterID,
  blotterPersistID = blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  defaultSort = '-Timestamp',
  persistFilter,
  initialIsOpen,
  onCloneTab,
  showAllDecimals,
  setShowAllDecimals,
}: MarketExecutionReportsBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const mixpanel = useMixpanel();
  const columns = useMarketExecutionReportColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    {
      persistFilter,
    }
  );

  const marketExecutionReportFilter = useMarketExecutionReportFilter({
    persistedBlotterTable,
    includeStatusFilter: true,
    includeIDFilter: parentOrderID == null,
  });
  const {
    clientSideFilter: clientLocalFilter,
    blotterTableFilterProps,
    filterBuilderProps,
  } = marketExecutionReportFilter;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });
  const marketExecutionReportMenu = useMarketExecutionReportMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
  });

  const columnsWithMenu = useMemo(
    () => [...columns, ...marketExecutionReportMenu.columns],
    [columns, marketExecutionReportMenu.columns]
  );
  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const { showBlotterPauseButton } = useFeatureFlag();

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const request = useMemo(
    () => ({
      name: MARKET_EXECUTION_REPORT,
      tag: blotterID,
      ...(parentOrderID != null && { ParentOrderID: parentOrderID }),
      ...(parentRFQID != null && { ParentRFQID: parentRFQID }),
      SendMarkets: true,
      ...removeEmptyFilters(onlyServerFilterKeys(marketExecutionReportFilter.filter)),
    }),
    [blotterID, parentOrderID, parentRFQID, marketExecutionReportFilter.filter]
  );

  // This call owns the true filter state
  const blotterTable = useWsBlotterTable({
    request,
    persistence: persistedBlotterTable,
    rowID: MarketExecutionReport.rowID,
    clientLocalFilter,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    columns: columnsWithMenu,
    getExtraMainMenuItems,
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    settings: blotterTableSettings,
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems: marketExecutionReportMenu.getContextMenuItems,
    },
    exportOptions: {
      name: 'MarketExecutionReports',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            {!showAllDecimalsInBlotters && setShowAllDecimals && (
              <Toggle
                size={FormControlSizes.Small}
                checked={showAllDecimals ?? false}
                onChange={setShowAllDecimals}
                label="Show All Decimals"
              />
            )}
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.Timestamp} />
      <BlotterTable {...blotterTable} />
      {marketExecutionReportMenu.dialogs}
    </>
  );
}
